import type { AutomaticAccessorial, ManualAccessorial, TransactionDetail } from '@/api/useFreight/types'
import { PlusOutlined } from '@ant-design/icons'
import { GraviButton, GraviGrid, Horizontal, MaterialIcon, Texto, Vertical } from '@gravitate-js/excalibrr'
import type { ColDef, GridApi } from 'ag-grid-community'
import { Form, Modal, Select } from 'antd'
import { groupBy, startCase } from 'lodash'
import { useEffect, useMemo, useRef, useState } from 'react'
import { ActionCellRenderer } from './ActionCellRenderer'
import { useViewAccessorials } from './useViewAccessorials'
import { AddAccessorialPopover } from './AddAccessorialPopover'
import { isDefined } from '@/utils/general'
import type { InferRequest } from '@/hooks/useApiClient'
import { useForm } from 'antd/es/form/Form'
import { OverrideValueCellRenderer } from '@/components/Freight/shared/LineItems/components/OverrideValueCellRenderer'
import { getDestinationsColumnDef, getOriginsColumnDef } from '@/components/Freight/shared'
import { AccessorialFormType } from '@/components/Freight/FreightLineItems/components/AddFreightLineItem/types'
import { useWizardMaster } from '@/components/OrderWizard/Contexts/WizardMasterContext'

type OverrideAccessorialReq = InferRequest<'/freight/transaction/order_dispatch/override'>

type ViewAccessorialsPopoverProps = {
  orderId: string
  freightCustomers: Gravitate.Order['freight_customers']
  isComplete: boolean
}

export function ViewAccessorialsModal({ orderId, freightCustomers, isComplete }: ViewAccessorialsPopoverProps) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <GraviButton size='large' appearance='outlined' onClick={() => setOpen(true)}>
        <MaterialIcon icon='event_available' /> View Accessorials
      </GraviButton>
      <Modal title='Accessorials' open={open} footer={null} width={820} destroyOnClose onCancel={() => setOpen(false)}>
        <ViewAccessorialsModalContent orderId={orderId} freightCustomers={freightCustomers} isComplete={isComplete} />
      </Modal>
    </>
  )
}

type ViewAccessorialsModalContentProps = {
  orderId: string
  freightCustomers: Gravitate.Order['freight_customers']
  isComplete: boolean
}

function ViewAccessorialsModalContent({ orderId, freightCustomers, isComplete }: ViewAccessorialsModalContentProps) {
  const [form] = useForm()
  const { wizardOrder } = useWizardMaster()
  const {
    useAvailableAccessorialsQuery,
    useTransactionOverviewQuery,
    useOrderAddManualAccessorialMutation,
    useOrderOverrideAccessorialMutation,
    overrideMutateAsync,
    addManualMutateAsync,
  } = useViewAccessorials()
  const { data: transactionOverview, isLoading } = useTransactionOverviewQuery({
    orderId,
  })
  const transactionsByCustomer = useMemo(
    () => groupBy(transactionOverview, (transaction) => transaction.counterparty_id || 'unknown_customer'),
    [transactionOverview]
  )

  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(freightCustomers?.[0]?.id || null)

  const { data: availableAccessorialsUnfiltered } = useAvailableAccessorialsQuery({
    orderId,
    actual: isComplete,
    customerId: selectedCustomerId,
  })

  const gridRef = useRef<GridApi<any>>(null)

  function handleSaveOverride(formValues: AccessorialFormType, accessorial: TransactionDetail) {
    if (!accessorial) return

    overrideMutateAsync(formValues, accessorial, isComplete, selectedCustomerId)
  }

  function handleAddAccessorial() {
    const values = form.getFieldsValue()
    if (!values.accessorial_id) return
    const selectedAccessorial = availableAccessorialsUnfiltered?.find((a) => a._id === values.accessorial_id)
    addManualMutateAsync(orderId, selectedAccessorial, values, isComplete, selectedCustomerId)
  }

  const columnDefs = useMemo(
    () =>
      getColumnDefs(
        handleSaveOverride,
        useOrderOverrideAccessorialMutation.isPending,
        useOrderOverrideAccessorialMutation.variables?.body
      ),
    [useOrderOverrideAccessorialMutation.isPending, useOrderOverrideAccessorialMutation.variables]
  )

  useEffect(() => {
    if (gridRef.current) {
      gridRef.current.refreshCells({ force: true })
    }
  }, [transactionOverview])

  return (
    <Vertical className='mt-2' gap={16}>
      <Vertical style={{ height: '400px', overflowY: 'auto', minWidth: 600 }}>
        <GraviGrid
          externalRef={gridRef}
          rowData={transactionsByCustomer?.[selectedCustomerId || 'unknown_customer'] || []}
          showColumnsToolbar={false}
          agPropOverrides={{ rowGroupPanelShow: 'never', context: { order: wizardOrder } }}
          sideBar={false}
          columnDefs={columnDefs}
          loading={isLoading}
          controlBarProps={{
            actionButtons: (
              <Horizontal gap={8} verticalCenter>
                <Texto category='p2'>Customer:</Texto>
                <Select
                  style={{ width: 300 }}
                  placeholder='Select Customer'
                  value={selectedCustomerId || undefined}
                  onChange={setSelectedCustomerId}
                  options={freightCustomers.map((c) => {
                    return {
                      label: c.name,
                      value: c.id,
                    }
                  })}
                  disabled={freightCustomers.length <= 1}
                />
              </Horizontal>
            ),
          }}
        />
      </Vertical>

      <Horizontal justifyContent='space-between' className='pt-3'>
        <AddAccessorialPopover
          form={form}
          order={wizardOrder}
          isAccessorialMutationPending={useOrderAddManualAccessorialMutation.isPending}
          accessorialOptions={availableAccessorialsUnfiltered?.filter((a) => !a.disabled) || []}
          handleAdd={handleAddAccessorial}
        >
          <GraviButton appearance='outlined' loading={useOrderAddManualAccessorialMutation.isPending}>
            <PlusOutlined /> Add Manual Accessorial
          </GraviButton>
        </AddAccessorialPopover>
      </Horizontal>
    </Vertical>
  )
}

const getColumnDefs = (
  handleSaveOverride: (formValues: AccessorialFormType, accessorial: TransactionDetail) => void,
  isOverridePending: boolean,
  isOverridePendingBody: OverrideAccessorialReq | null
): ColDef[] => [
  {
    field: 'subtype',
    headerName: 'Accessorial',
    minWidth: 200,
    enableRowGroup: false,
  },
  {
    field: 'manual',
    headerName: 'Type',
    cellRenderer: (params) => (params.value ? 'Manual' : 'Automatic'),
    valueFormatter: (params) => (params.value ? 'Manual' : 'Automatic'),
    enableRowGroup: false,
  },
  {
    ...getOriginsColumnDef(),
  },
  {
    ...getDestinationsColumnDef(),
  },
  {
    field: 'uom',
    headerName: 'UoM',
    valueFormatter: (params) => startCase(params.value),
    enableRowGroup: false,
  },
  {
    field: 'amount',
    headerName: 'Amount',
    valueFormatter: (params) => (params.value ? `$${params.value.toFixed(2)}` : ''),
    cellRenderer: OverrideValueCellRenderer,
    enableRowGroup: false,
    cellStyle: ({ data }) => ({
      backgroundColor: data?.amount_override !== null ? 'var(--theme-warning-dim)' : 'transparent',
    }),
  },
  {
    headerName: '',
    field: 'actions',
    cellRenderer: ({ data, context }) => {
      return (
        <ActionCellRenderer
          handleSave={handleSaveOverride}
          data={data}
          isOverridePending={isOverridePending && data.id === isOverridePendingBody?.transaction_id}
          order={context.order}
        />
      )
    },
    enableRowGroup: false,
    hide: false,
    minWidth: 60,
    maxWidth: 60,
    pinned: 'right',
  },
]
